import { IFilterFacet } from "../interfaces/interfaces";

const SUPPORTED_COMPONENTS = new Set([
  "CheckboxFilter",
  "OntologyFilter",
  "ToggleFilter",
  "ServiceFilter",
  "StringFilter",
]);

function normalizeFacetIdentifier(facet: IFilterFacet, index: number): string {
  const explicitIdentifier = facet.facetIdentifier?.trim();
  if (explicitIdentifier) {
    return explicitIdentifier.replaceAll(" ", "");
  }

  if (facet.facetTitle?.trim()) {
    return facet.facetTitle.trim().replaceAll(" ", "");
  }

  if (facet.applyToColumn?.trim()) {
    return facet.applyToColumn.trim().replaceAll(" ", "");
  }

  return `facet-${index + 1}`;
}

function isSortDirectionValid(direction: unknown): boolean {
  if (direction === undefined) return true;
  if (typeof direction !== "string") return false;

  const normalizedDirection = direction.toLowerCase();
  return normalizedDirection === "asc" || normalizedDirection === "desc";
}

function requiresApplyToColumn(component: string): boolean {
  return component !== "StringFilter";
}

export function validateFilterFacets(filterFacets: IFilterFacet[]): string[] {
  const errors: string[] = [];
  const seenFacetIdentifiers = new Set<string>();

  filterFacets.forEach((facet, index) => {
    const facetLabel = facet.facetTitle || `Filter ${index + 1}`;
    const facetIdentifier = normalizeFacetIdentifier(facet, index);

    if (!facet.facetTitle?.trim()) {
      errors.push(`Filter ${index + 1}: facetTitle is required.`);
    }

    if (!facet.component?.trim()) {
      errors.push(`${facetLabel}: component is required.`);
    } else if (!SUPPORTED_COMPONENTS.has(facet.component)) {
      errors.push(
        `${facetLabel}: component '${facet.component}' is not supported.`
      );
    }

    if (seenFacetIdentifiers.has(facetIdentifier)) {
      errors.push(
        `${facetLabel}: duplicate facet identifier '${facetIdentifier}'.`
      );
    } else {
      seenFacetIdentifiers.add(facetIdentifier);
    }

    if (requiresApplyToColumn(facet.component) && !facet.applyToColumn?.trim()) {
      errors.push(`${facetLabel}: applyToColumn is required.`);
    }

    if (!isSortDirectionValid(facet.sortDirection)) {
      errors.push(
        `${facetLabel}: sortDirection must be 'asc' or 'desc' when provided.`
      );
    }

    if (facet.component === "ToggleFilter") {
      if (!facet.trueOption) {
        errors.push(`${facetLabel}: trueOption is required for ToggleFilter.`);
      } else if (!facet.trueOption.text?.trim()) {
        errors.push(`${facetLabel}: trueOption.text is required.`);
      }
    }
  });

  return errors;
}

const DATA_MODEL_TABLE_KEYS = [
  "providerTable",
  "serviceTable",
  "networkTable",
  "providerRefOnService",
];

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.every((entry) => typeof entry === "string" && entry.trim() !== "")
  );
}

export function validateDataModel(dataModel: unknown): string[] {
  const errors: string[] = [];

  if (!dataModel || typeof dataModel !== "object" || Array.isArray(dataModel)) {
    return ["dataModel must be a JSON object."];
  }

  const dataModelObject = dataModel as Record<string, unknown>;

  for (const key of DATA_MODEL_TABLE_KEYS) {
    const value = dataModelObject[key];
    if (typeof value !== "string" || !value.trim()) {
      errors.push(`dataModel: ${key} must be a non-empty string.`);
    }
  }

  const searchFields = dataModelObject.searchFields;
  if (
    !searchFields ||
    typeof searchFields !== "object" ||
    Array.isArray(searchFields)
  ) {
    errors.push("dataModel: searchFields must be a JSON object.");
  } else {
    const searchFieldsObject = searchFields as Record<string, unknown>;
    if (
      !isStringArray(searchFieldsObject.root) ||
      searchFieldsObject.root.length === 0
    ) {
      errors.push(
        "dataModel: searchFields.root must be a non-empty array of strings."
      );
    }

    for (const [branch, fields] of Object.entries(searchFieldsObject)) {
      if (branch === "root") continue;
      if (!isStringArray(fields)) {
        errors.push(
          `dataModel: searchFields.${branch} must be an array of strings.`
        );
      }
    }
  }

  return errors;
}

const SUPPORTED_SERVICE_COLUMN_TYPES = new Set([
  "string",
  "longtext",
  "hyperlink",
  "email",
  "ref",
  "mref",
  "bool",
]);

/**
 * Validates a service display config (serviceReportColumns /
 * serviceProviderReportColumns).
 *
 * These keys used to hold a raw GraphQL selection (bare strings and nested
 * objects) rather than {label, column} entries. A persisted config in that
 * legacy shape is reported here so loadConfig can fall back to the code
 * defaults instead of rendering an empty page.
 */
export function validateServiceColumns(
  columns: unknown,
  configKey: string
): string[] {
  if (!Array.isArray(columns)) {
    return [`${configKey} must be an array.`];
  }

  const errors: string[] = [];

  columns.forEach((column, index) => {
    const position = `${configKey}[${index}]`;

    if (typeof column === "string") {
      errors.push(
        `${position}: legacy GraphQL selection entry '${column}'. Expected { label, column } - see README "Service report configuration".`
      );
      return;
    }

    if (!column || typeof column !== "object" || Array.isArray(column)) {
      errors.push(`${position} must be a JSON object.`);
      return;
    }

    const columnObject = column as Record<string, unknown>;

    if (
      typeof columnObject.column !== "string" ||
      !columnObject.column.trim() ||
      typeof columnObject.label !== "string" ||
      !columnObject.label.trim()
    ) {
      errors.push(
        `${position}: label and column are required non-empty strings.`
      );
      return;
    }

    const type = columnObject.type;
    if (type !== undefined) {
      if (typeof type !== "string" || !SUPPORTED_SERVICE_COLUMN_TYPES.has(type)) {
        errors.push(`${position}: type '${String(type)}' is not supported.`);
      }
    }

    if (
      columnObject.refFields !== undefined &&
      !isStringArray(columnObject.refFields)
    ) {
      errors.push(`${position}: refFields must be an array of strings.`);
    }

    if (
      columnObject.subColumn !== undefined &&
      (typeof columnObject.subColumn !== "string" ||
        !columnObject.subColumn.trim())
    ) {
      errors.push(`${position}: subColumn must be a non-empty string.`);
    }
  });

  return errors;
}

export function validateDirectoryConfiguration(config: unknown): string[] {
  if (!config || typeof config !== "object") {
    return ["Configuration payload must be a JSON object."];
  }

  const configObject = config as {
    filterFacets?: IFilterFacet[];
    dataModel?: unknown;
    serviceReportColumns?: unknown;
    serviceProviderReportColumns?: unknown;
  };
  if (!Array.isArray(configObject.filterFacets)) {
    return ["Configuration must contain a filterFacets array."];
  }

  const errors = validateFilterFacets(configObject.filterFacets);

  // dataModel is optional: when absent, the code defaults apply.
  if (configObject.dataModel !== undefined) {
    errors.push(...validateDataModel(configObject.dataModel));
  }

  // Same for the service display configs.
  if (configObject.serviceReportColumns !== undefined) {
    errors.push(
      ...validateServiceColumns(
        configObject.serviceReportColumns,
        "serviceReportColumns"
      )
    );
  }

  if (configObject.serviceProviderReportColumns !== undefined) {
    errors.push(
      ...validateServiceColumns(
        configObject.serviceProviderReportColumns,
        "serviceProviderReportColumns"
      )
    );
  }

  return errors;
}
